import { useState } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { baseApiURL } from "../../../baseUrl";
import toast from "react-hot-toast";
import { FiUpload, FiDownload } from "react-icons/fi";

const ImportStudent = () => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [batchSize, setBatchSize] = useState(10); // Default batch size
  const [overwrite, setOverwrite] = useState(true); // Allow overwrite existing

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
    }
  };

  const downloadTemplateHandler = () => {
    toast.loading("Downloading template file");
    // Create template with 5 sample records
    const currentYear = new Date().getFullYear();
    const templateData = Array.from({ length: 5 }, (_, i) => ({
      enrollmentNo: `22N81A05${String(i + 1).padStart(2, '0')}`,
      firstName: `First${i + 1}`,
      middleName: `M${i + 1}`,
      lastName: `Last${i + 1}`,
      email: `student${i + 1}@example.com`,
      phoneNumber: `98765432${10 + i}`,
      FatherName: `Father${i + 1}`,
      MotherName: `Mother${i + 1}`,
      FatherPhoneNumber: `98765432${20 + i}`,
      MotherPhoneNumber: `98765432${30 + i}`,
      semester: `${(i % 8) + 1}`,
      branch: ["CSE", "ECE", "MECH", "CIVIL", "EEE"][i % 5],
      batch: `${currentYear - (i % 4)}`,
      regulation: ["R18", "R20", "R21", "R22"][i % 4],
      gender: i % 2 === 0 ? "Male" : "Female",
    }));
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Student Template");
    XLSX.writeFile(wb, "Student_Import_Template.xlsx");
    toast.dismiss();
    toast.success("Template downloaded successfully!");
  };

  // Process a single student
  const processSingleStudent = async (student) => {
    try {
      // Add a small delay to prevent overwhelming the server with rapid concurrent-like requests
      await new Promise(resolve => setTimeout(resolve, 50));

      const enrollmentNo = student.enrollmentNo || student.enrollment || student.loginid;
      if (!enrollmentNo) {
        return { success: false, message: "Missing required field 'enrollmentNo'" };
      }
      // Normalize student object for the backend
      student.enrollmentNo = enrollmentNo;

      if (!student.batch) {
        return { success: false, message: "Missing required field 'batch'" };
      }
      if (!student.regulation) {
        return { success: false, message: "Missing required field 'regulation'" };
      }
      const formData = new FormData();
      Object.entries(student).forEach(([key, value]) => {
        formData.append(key, key === "regulation" ? String(value).toUpperCase() : value);
      });
      formData.append('type', 'excel-import');
      formData.append('overwrite', overwrite ? 'true' : 'false');
      // Add student details
      const detailsResponse = await axios.post(
        `${baseApiURL()}/student/details/addDetails`,
        formData
      );
      if (detailsResponse.data.success) {
        try {
          // Create credentials (idempotent): ignore if already exists
          await axios.post(`${baseApiURL()}/student/auth/register`, {
            loginid: student.enrollmentNo,
            password: student.enrollmentNo,
          });
        } catch (regErr) {
          const msg = regErr?.response?.data?.message?.toString().toLowerCase() || "";
          const isAlreadyExists = msg.includes("already exists") || msg.includes("exists");
          if (!isAlreadyExists) {
            // Registration failed for another reason
            return { success: false, message: regErr?.response?.data?.message || regErr.message };
          }
        }
        return { success: true };
      }
      return { success: false, message: detailsResponse.data.message };
    } catch (error) {
      console.error("Single student process error:", error);
      const errorMessage = error.response?.data?.message || error.message;
      return {
        success: false,
        message: error.message === "Network Error" ? "Network Error: Server might be busy or unreachable. Try reducing batch size." : errorMessage,
      };
    }
  };

  // Process a batch of students
  const processBatch = async (batch, startIndex) => {
    const results = [];
    for (let i = 0; i < batch.length; i++) {
      const result = await processSingleStudent(batch[i]);
      results.push({
        index: startIndex + i,
        success: result.success,
        message: result.message || (result.success ? "Success" : "Failed"),
        enrollmentNo: batch[i].enrollmentNo,
      });
    }
    return results;
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file first!");
      return;
    }
    setIsLoading(true);
    toast.loading(`Processing student data in batches of ${batchSize}...`);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawData = XLSX.utils.sheet_to_json(worksheet);
      
      // Filter out empty rows or rows missing enrollmentNo
      const jsonData = rawData.filter(row => row.enrollmentNo || row.enrollment || row.loginid);

      if (jsonData.length === 0) {
        toast.dismiss();
        setIsLoading(false);
        return toast.error("No valid student records found in the Excel file!");
      }
      let successCount = 0;
      let errorCount = 0;
      const errorMessages = [];
      const totalBatches = Math.ceil(jsonData.length / batchSize);
      // Process in batches
      for (let i = 0; i < jsonData.length; i += batchSize) {
        const batch = jsonData.slice(i, i + batchSize);
        const batchResults = await processBatch(batch, i + 2); // +2 for header row and 1-based index
        for (let j = 0; j < batchResults.length; j++) {
          const result = batchResults[j];
          if (result.success) {
            successCount++;
          } else {
            errorCount++;
            const identifier = result.enrollmentNo || "Unknown Enrollment";
            errorMessages.push(`Row ${result.index}: ${identifier} - ${result.message}`);
          }
        }
        // Update progress
        const currentBatch = Math.ceil((i + batchSize) / batchSize);
        toast.loading(`Processing batch ${currentBatch} of ${totalBatches}...`);
      }
      toast.dismiss();
      setIsLoading(false);
      if (errorCount > 0) {
        toast.error(
          `Processed ${successCount} students, ${errorCount} errors. See console for details.`,
          { duration: 8000 }
        );
        console.error("Import errors:", errorMessages);
      } else {
        toast.success(`Successfully imported ${successCount} students!`);
      }
      setFile(null);
      setFileName("");
    } catch (error) {
      console.error("Error processing file:", error);
      toast.dismiss();
      setIsLoading(false);
      toast.error("Failed to process student data!");
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      {/* Top Header Banner */}
      <div className="bento-header-banner flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 p-2.5 rounded-xl">
            <FiUpload className="text-indigo-600 dark:text-indigo-400 text-xl" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Bulk Student Import</h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium text-xs mt-0.5">Upload Excel spreadsheets to register multiple student records</p>
          </div>
        </div>
      </div>

      <div className="bento-card p-6 md:p-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <button
              onClick={downloadTemplateHandler}
              className="flex items-center justify-center w-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold py-2.5 px-4 rounded-xl shadow-xs transition mb-4 cursor-pointer"
            >
              <FiDownload className="mr-2" />
              Download Template
            </button>
            <div className="border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl p-6 text-center bg-slate-50 dark:bg-slate-950">
              <div className="flex flex-col items-center justify-center">
                <FiUpload className="text-4xl text-slate-400 dark:text-slate-500 mb-2" />
                <p className="text-slate-600 dark:text-slate-400 text-xs mb-3">
                  {fileName || "Drag & drop your Excel file here or click to browse"}
                </p>
                <input
                  type="file"
                  id="file-upload"
                  accept=".xlsx, .xls"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs font-bold py-2 px-4 rounded-xl transition"
                >
                  Browse Files
                </label>
              </div>
            </div>
            {fileName && (
              <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-950/40 rounded-xl border border-indigo-100 dark:border-indigo-800">
                <p className="text-xs font-bold text-slate-900 dark:text-white">Selected file: {fileName}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Batch size: {batchSize} records at a time
                </p>
              </div>
            )}
          </div>
          <div>
            <div className="mb-4">
              <label className="inline-flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" className="rounded border-slate-300 dark:border-slate-700 text-indigo-600 focus:ring-indigo-500" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Overwrite existing students on import</span>
              </label>
            </div>
            <div className="mb-4">
              <label htmlFor="batch-size" className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                Records per batch
              </label>
              <select
                id="batch-size"
                value={batchSize}
                onChange={(e) => setBatchSize(Number(e.target.value))}
                className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl text-xs focus:ring-2 focus:ring-indigo-500"
              >
                <option value="5">5 records</option>
                <option value="10">10 records</option>
                <option value="20">20 records</option>
                <option value="50">50 records</option>
                <option value="100">100 records</option>
                <option value="200">200 records</option>
                <option value="500">500 records</option>
              </select>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                Larger batches are faster but may overload your server
              </p>
            </div>
            <button
              onClick={handleImport}
              disabled={!file || isLoading}
              className={`w-full py-2.5 px-4 rounded-xl text-white text-xs font-bold shadow-xs cursor-pointer ${
                !file || isLoading
                  ? "bg-slate-400 opacity-50 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-500"
              } transition`}
            >
              {isLoading ? "Processing..." : `Import Students (Batch size: ${batchSize})`}
            </button>
            <div className="mt-5 p-4 bg-amber-50 dark:bg-amber-950/40 rounded-xl border border-amber-200 dark:border-amber-800">
              <h3 className="font-bold text-xs text-amber-800 dark:text-amber-300 mb-1.5">Batch Import Tips:</h3>
              <ul className="text-xs text-amber-700 dark:text-amber-400 list-disc pl-4 space-y-1">
                <li>Start with smaller batches (5-10) for testing</li>
                <li>Increase batch size for faster imports with stable connections</li>
                <li>Monitor server performance during large imports</li>
                <li>Check console for detailed error reports</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportStudent; 